/**
 * Pure entry-scroll helpers for ShotLane (Timeline tab entry).
 * No imports, no side effects — all logic is expressed as pure functions
 * that accept measured geometry/store values so they can be unit-tested
 * without jsdom layout (jsdom reports 0 for clientWidth/scrollLeft).
 *
 * Companion to `SceneBoard/sceneBoardScroll.ts`, which owns the Board ⇄ Timeline
 * scene-anchor geometry. These two decide who owns the scroll on mount.
 */

/**
 * Does a mounting ShotLane have a selected shot it can legally center on?
 *
 * Mirrors the guards inside ShotLane's selected-shot centering effect:
 *   - no selection                                    → false (anchor owns entry)
 *   - Timeline scoped to one scene (activeSceneId set) and the selected shot
 *     lives in a DIFFERENT scene                      → false (the effect bails
 *     there too: seek() would clamp into the scoped window and the scroll math
 *     would target a nonsensical position)
 *   - otherwise                                       → true
 *
 * `sceneIdOfSelectedShot` is null when the shot cannot be resolved in the
 * loaded project — treated as in-scope, matching the effect's `if (shotScene && …)`.
 */
export function selectionOwnsEntryScroll(args: {
  selectedShotId: number | null;
  activeSceneId: number | null;
  sceneIdOfSelectedShot: number | null;
}): boolean {
  const { selectedShotId, activeSceneId, sceneIdOfSelectedShot } = args;
  if (selectedShotId == null) return false;
  if (activeSceneId != null && sceneIdOfSelectedShot != null
      && sceneIdOfSelectedShot !== activeSceneId) return false;
  return true;
}

/**
 * True when [startPx, endPx] sits entirely inside [currentOffset, currentOffset + viewportSize].
 * A range wider than the viewport is never fully visible → false.
 */
export function isRangeFullyVisible(args: {
  startPx: number;
  endPx: number;
  viewportSize: number;
  currentOffset: number;
}): boolean {
  const { startPx, endPx, viewportSize, currentOffset } = args;
  return startPx >= currentOffset && endPx <= currentOffset + viewportSize;
}
